import { useCallback, useState } from 'react';

import { PaginationState, RowSelectionState, SortingState } from '@tanstack/react-table';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';

import { SettingsHeader } from './components/settings-header';
import { Permission } from '../../../../../../../backend/src/auth/permissions/permission';
import { MatterDocsWithCasesFilters } from '../../../../../../../backend/src/matter/doc/dto/doc-with-cases.response.dto';
import { DocsTable } from '../docs/components/docs-table';
import { useGetMatterDocsWithCases } from '@/api/queries/useGetMatterDocsWithCases';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useUserContext } from '@/Contexts/User';
import { useHasPermission } from '@/hooks/useHasPermission';
import { FilterPills } from '@/new/components/filter-pills';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { FilterPill, ATTRIBUTE_COLOURS } from '@/utils/buildFilterPills';

export function DocumentManagement() {
  const { matterId } = useParams();
  if (!matterId) return null;

  const { user } = useUserContext();
  const { hasPermission } = useHasPermission();
  const [searchParams, setSearchParams] = useSearchParams();

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 50 });
  const [sorting, setSorting] = useState<SortingState>([{ id: 'file.name', desc: false }]);
  const [filters, setFilters] = useState<MatterDocsWithCasesFilters>(() => {
    // Initialize filters from URL params
    const processedParam = searchParams.get('processed');
    if (processedParam === 'true') return { processed: true };
    if (processedParam === 'false') return { processed: false };
    return {};
  });

  const [localSearchQuery, setLocalSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  const debouncedSetSearchQuery = useDebouncedCallback((value: string) => {
    setDebouncedSearchQuery(value);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, 300);

  const handleSearchQueryChange = useCallback(
    (query: string) => {
      setLocalSearchQuery(query);
      debouncedSetSearchQuery(query);
    },
    [debouncedSetSearchQuery],
  );

  const handleFiltersChange = useCallback(
    (newFilters: MatterDocsWithCasesFilters) => {
      setFilters(newFilters);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));

      // Sync processed filter with URL
      if (newFilters.processed === true) {
        setSearchParams({ processed: 'true' });
      } else if (newFilters.processed === false) {
        setSearchParams({ processed: 'false' });
      } else {
        setSearchParams({});
      }
    },
    [setSearchParams],
  );

  const handleRemoveFilter = useCallback(
    (filterKey: string) => {
      setFilters((prev) => {
        const updated = { ...prev };
        delete updated[filterKey as keyof MatterDocsWithCasesFilters];
        return updated;
      });
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));

      // Clear URL param when removing processed filter
      if (filterKey === 'processed') {
        setSearchParams({});
      }
    },
    [setSearchParams],
  );

  const handleClearAllFilters = useCallback(() => {
    setFilters({});
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    setSearchParams({});
  }, [setSearchParams]);

  // Build filter pills
  const filterPills: FilterPill[] = [];
  if (filters.processed === true) {
    filterPills.push({
      id: 'processed-true',
      label: 'Processed',
      color: ATTRIBUTE_COLOURS.processed,
      onRemove: () => handleRemoveFilter('processed'),
    });
  } else if (filters.processed === false) {
    filterPills.push({
      id: 'processed-false',
      label: 'Not Processed',
      color: ATTRIBUTE_COLOURS.processed,
      onRemove: () => handleRemoveFilter('processed'),
    });
  }

  const handleSortingChange = useCallback((updaterOrValue: SortingState | ((old: SortingState) => SortingState)) => {
    setSorting(updaterOrValue);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  // Convert sorting state to API params
  const sortBy = sorting.length > 0 ? (sorting?.[0]?.id?.split('.').pop() ?? undefined) : undefined;
  const sortOrder = sorting.length > 0 ? (sorting?.[0]?.desc ? 'DESC' : 'ASC') : undefined;

  const { data: matter } = useGetMatterV2(matterId);

  // User can delete documents if they are the matter creator OR have the MatterDeleteDocument permission
  const isMatterCreator = matter?.createdById === user?.id;
  const canDeleteDocuments = isMatterCreator || hasPermission(Permission.MatterDeleteDocument);

  const { data, isLoading } = useGetMatterDocsWithCases(matterId, {
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filters: { ...filters, searchQuery: debouncedSearchQuery || undefined },
    sortBy,
    sortOrder,
  });

  return (
    <>
      <SettingsHeader
        title="Document Management"
        subheading={
          <p className="text-sm text-gray-500">
            View and manage all documents uploaded to {matter?.name ?? 'this matter'}.
          </p>
        }
        action={
          <Button asChild variant="primary">
            <Link to={`/matters/${matterId}/docs/upload`}>
              <Icon className="lucide--file-up" />
              <span>Upload Documents</span>
            </Link>
          </Button>
        }
      />

      <DocsTable
        docs={data?.docs}
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        isLoading={isLoading}
        pagination={pagination}
        onPaginationChange={setPagination}
        pageCount={data?.pageCount ?? 0}
        totalCount={data?.totalCount ?? 0}
        searchQuery={localSearchQuery}
        onSearchQueryChange={handleSearchQueryChange}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        filterPills={
          filterPills.length > 0 ? (
            <FilterPills pills={filterPills} onClearAll={handleClearAllFilters} />
          ) : undefined
        }
        sorting={sorting}
        onSortingChange={handleSortingChange}
        canDeleteDocuments={canDeleteDocuments}
      />
    </>
  );
}
